import React, { useEffect, useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { InputSwitch } from 'primereact/inputswitch';
import { Slider } from 'primereact/slider';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { playSound, showDesktopNotification } from '../utils/notificationSound';
import { notificationsAPI } from '../services/api';
import { showCustomAlert } from './CustomAlert';

const STORAGE_KEYS = {
  soundEnabled: 'uptimio_notifications_sound_enabled',
  soundVolume: 'uptimio_notifications_sound_volume',
  desktopEnabled: 'uptimio_notifications_desktop_enabled'
};

const NotificationSettings: React.FC = () => {
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [soundVolume, setSoundVolume] = useState(50);
  const [desktopEnabled, setDesktopEnabled] = useState(false);
  const [testStatus, setTestStatus] = useState<string | null>(null);

  useEffect(() => {
    const se = localStorage.getItem(STORAGE_KEYS.soundEnabled);
    const sv = localStorage.getItem(STORAGE_KEYS.soundVolume);
    const de = localStorage.getItem(STORAGE_KEYS.desktopEnabled);

    if (se !== null) setSoundEnabled(se === 'true');
    if (sv !== null) setSoundVolume(Number(sv));

    // Ako korisnik već ima dozvolu u browseru, podigni switch automatski
    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
      setDesktopEnabled(true);
      localStorage.setItem(STORAGE_KEYS.desktopEnabled, 'true');
    } else if (de !== null) {
      setDesktopEnabled(de === 'true');
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.soundEnabled, String(soundEnabled));
  }, [soundEnabled]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.soundVolume, String(soundVolume));
  }, [soundVolume]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.desktopEnabled, String(desktopEnabled));
  }, [desktopEnabled]);

  const volumeLabel = useMemo(() => `${soundVolume}%`, [soundVolume]);

  const handleRequestDesktopPermission = async () => {
    if (!('Notification' in window)) {
      setTestStatus('Ovaj pregledač ne podržava desktop notifikacije.');
      return;
    }

    if (Notification.permission === 'granted') {
      setDesktopEnabled(true);
      setTestStatus('Desktop notifikacije su već omogućene.');
      return;
    }

    const result = await Notification.requestPermission();
    if (result === 'granted') {
      setDesktopEnabled(true);
      setTestStatus('Desktop notifikacije su uspešno omogućene.');
    } else {
      setDesktopEnabled(false);
      setTestStatus('Desktop notifikacije nisu omogućene.');
    }
  };

  const playTestSound = () => {
    if (!soundEnabled) {
      setTestStatus('Uključite zvučne notifikacije da biste testirali zvuk.');
      return;
    }
    playSound('notification');
    setTestStatus('Test zvuk je pušten (proverite da li čujete notifikaciju).');
  };

  const sendTestEmail = async () => {
    setTestStatus(null);
    try {
      const response = await notificationsAPI.sendTestEmail();
      setTestStatus(`Test email je uspešno poslat na ${response.email}.`);
      showCustomAlert.success(`Test email je poslat na ${response.email}.`, 'Test email poslat');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Greška pri slanju test emaila';
      setTestStatus(`Greška: ${errorMessage}`);
      showCustomAlert.error(errorMessage, 'Greška');
    }
  };

  const testDesktopNotification = () => {
    if (!('Notification' in window)) {
      setTestStatus('Ovaj pregledač ne podržava desktop notifikacije.');
      return;
    }

    if (Notification.permission !== 'granted') {
      setTestStatus('Morate prvo omogućiti desktop notifikacije i dati dozvolu pregledaču.');
      return;
    }

    // Zvuk će se automatski reprodukovati u showDesktopNotification
    showDesktopNotification('Test notifikacija', {
      body: 'Ovo je test desktop notifikacija iz Thermowool ERP-a.',
      icon: '/favicon.ico',
    });
    setTestStatus('Test desktop notifikacija je poslata (proverite sistemske notifikacije).');
  };

  return (
    <div className="p-4">
      <h2>Podešavanja Notifikacija</h2>

      {testStatus && (
        <div className="mb-3">
          <Message
            severity="info"
            text={testStatus}
            onClick={() => setTestStatus(null)}
          />
        </div>
      )}

      <div className="grid">
        {/* Zvucne notifikacije */}
        <div className="col-12">
          <Card>
            <div className="flex align-items-center gap-2 mb-3">
              <span className="text-2xl">🔊</span>
              <h3 className="m-0">Zvučne Notifikacije</h3>
            </div>

            <div className="flex align-items-center gap-2 mb-3">
              <InputSwitch
                checked={soundEnabled}
                onChange={(e) => setSoundEnabled(!!e.value)}
              />
              <span>Omogući zvučne notifikacije</span>
            </div>

            <div className="mb-2">Jačina zvuka: {volumeLabel}</div>
            <Slider
              value={soundVolume}
              onChange={(e) => setSoundVolume(e.value as number)}
              min={0}
              max={100}
            />
          </Card>
        </div>

        {/* Desktop notifikacije */}
        <div className="col-12">
          <Card>
            <div className="flex align-items-center gap-2 mb-3">
              <span className="text-2xl">⚠️</span>
              <h3 className="m-0">Desktop Notifikacije</h3>
            </div>

            <p className="mb-3">
              Kliknite na dugme ispod da omogućite desktop notifikacije.
            </p>

            <div className="flex align-items-center gap-2 mb-3">
              <Button
                label="Omogući desktop notifikacije"
                icon="pi pi-desktop"
                onClick={handleRequestDesktopPermission}
                outlined
              />
              <InputSwitch
                checked={desktopEnabled}
                onChange={(e) => setDesktopEnabled(!!e.value)}
              />
              <span>Omogući desktop notifikacije</span>
            </div>
          </Card>
        </div>

        {/* Test email */}
        <div className="col-12">
          <Card>
            <div className="flex align-items-center gap-2 mb-3">
              <span className="text-2xl">📧</span>
              <h3 className="m-0">Test Email</h3>
            </div>

            <p className="mb-3">
              Pošaljite test email na vašu email adresu da proverite da li email servis radi ispravno.
            </p>

            <div className="flex gap-2">
              <Button
                label="Pusti test zvuk"
                icon="pi pi-volume-up"
                onClick={playTestSound}
                className="p-button-outlined"
              />
              <Button
                label="Test desktop notifikacija"
                icon="pi pi-desktop"
                onClick={testDesktopNotification}
                className="p-button-outlined"
              />
              <Button
                label="Pošalji Test Email"
                icon="pi pi-send"
                onClick={sendTestEmail}
              />
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;


